export interface VaccineTutorial {
  type: "video" | "image"
  src: string
  caption?: string
}

export interface VaccineDetail {
  id: string
  name: string
  fullName: string
  description: string
  purpose: string
  targetDisease: string[]
  applicationMethods: {
    method: string
    description: string
    steps: string[]
    tutorials?: VaccineTutorial[]
  }[]
  dosage: string
  storage: string
  precautions: string[]
  sideEffects?: string[]
  notes?: string
}

export interface VaccineScheduleItem {
  id: string
  dayMin: number
  dayMax?: number
  weekMin?: number
  weekMax?: number
  vaccineId: string
  vaccineName: string
  vaccineType: string
  applicationMethod: string
  isRequired: boolean
  notes?: string
}

// Vaccine schedule based on user's image
export const vaccineSchedule: VaccineScheduleItem[] = [
  {
    id: "v1",
    dayMin: 7,
    vaccineId: "nd-ib-live",
    vaccineName: "ND IB IB var",
    vaccineType: "Live",
    applicationMethod: "Spray/tetes",
    isRequired: true,
    notes: "Vaksin pertama untuk perlindungan awal Newcastle Disease dan Infectious Bronchitis",
  },
  {
    id: "v2",
    dayMin: 7,
    vaccineId: "nd-g7-ib-ai-killed",
    vaccineName: "ND G7 IB AI",
    vaccineType: "Killed",
    applicationMethod: "Sub Cutan",
    isRequired: true,
    notes: "Vaksin inaktif untuk membentuk antibodi jangka panjang",
  },
  {
    id: "v3",
    dayMin: 10,
    vaccineId: "gumboro-a",
    vaccineName: "Gumboro A",
    vaccineType: "Live",
    applicationMethod: "Cekok",
    isRequired: true,
    notes: "Perlindungan pertama terhadap penyakit Gumboro (IBD)",
  },
  {
    id: "v4",
    dayMin: 20,
    vaccineId: "gumboro-b",
    vaccineName: "Gumboro B",
    vaccineType: "Live",
    applicationMethod: "Cekok",
    isRequired: true,
    notes: "Booster vaksin Gumboro untuk perlindungan optimal",
  },
  {
    id: "v5",
    dayMin: 25,
    vaccineId: "pox",
    vaccineName: "POX",
    vaccineType: "Live",
    applicationMethod: "Tusuk sayap",
    isRequired: true,
    notes: "Perlindungan terhadap cacar unggas",
  },
  {
    id: "v6",
    dayMin: 30,
    vaccineId: "nd-g7-ib-ai-killed",
    vaccineName: "ND G7 IB AI",
    vaccineType: "Killed",
    applicationMethod: "Suntik dada",
    isRequired: true,
    notes: "Booster vaksin inaktif untuk meningkatkan kekebalan",
  },
  {
    id: "v7",
    dayMin: 30,
    vaccineId: "nd-ib-live",
    vaccineName: "ND IB",
    vaccineType: "Live",
    applicationMethod: "Cekok",
    isRequired: true,
    notes: "Booster vaksin live ND IB",
  },
  {
    id: "v8",
    dayMin: 37,
    vaccineId: "coryza",
    vaccineName: "Coryza",
    vaccineType: "Killed",
    applicationMethod: "Suntik dada",
    isRequired: true,
    notes: "Vaksin pertama untuk penyakit snot (Coryza)",
  },
  {
    id: "v9",
    dayMin: 52,
    vaccineId: "nd-ib-live",
    vaccineName: "ND IB",
    vaccineType: "Live",
    applicationMethod: "Air minum",
    isRequired: true,
    notes: "Vaksinasi massal melalui air minum",
  },
  {
    id: "v10",
    weekMin: 8,
    dayMin: 56,
    vaccineId: "ilt",
    vaccineName: "ILT",
    vaccineType: "Live",
    applicationMethod: "Cekok",
    isRequired: true,
    notes: "Perlindungan terhadap Infectious Laryngotracheitis",
  },
  {
    id: "v11",
    weekMin: 9,
    dayMin: 63,
    vaccineId: "nd-ib-live",
    vaccineName: "ND + IB",
    vaccineType: "Live",
    applicationMethod: "Air minum",
    isRequired: true,
    notes: "Vaksinasi booster rutin melalui air minum",
  },
  {
    id: "v12",
    weekMin: 11,
    dayMin: 77,
    vaccineId: "coryza",
    vaccineName: "Coryza 2",
    vaccineType: "Killed",
    applicationMethod: "Suntik paha",
    isRequired: true,
    notes: "Booster vaksin Coryza kedua",
  },
  {
    id: "v13",
    weekMin: 13,
    dayMin: 91,
    vaccineId: "triple-nd-ib-eds",
    vaccineName: "Triple ND + IB + EDS",
    vaccineType: "Killed",
    applicationMethod: "Suntik dada/paha",
    isRequired: true,
    notes: "Vaksin triple untuk layer sebelum produksi",
  },
  {
    id: "v14",
    weekMin: 14,
    dayMin: 98,
    vaccineId: "nd-ib-live",
    vaccineName: "ND + IB",
    vaccineType: "Live",
    applicationMethod: "Tetes mata",
    isRequired: true,
    notes: "Vaksinasi melalui tetes mata untuk penyerapan optimal",
  },
  {
    id: "v15",
    weekMin: 16,
    dayMin: 112,
    vaccineId: "nd-g7-ib-ai-killed",
    vaccineName: "ND G7 IB AI",
    vaccineType: "Killed",
    applicationMethod: "Suntik dada/paha",
    isRequired: true,
    notes: "Vaksinasi penguat sebelum periode produksi",
  },
  {
    id: "v16",
    weekMin: 17,
    dayMin: 119,
    vaccineId: "coryza",
    vaccineName: "Coryza 3",
    vaccineType: "Killed",
    applicationMethod: "Suntik paha",
    isRequired: true,
    notes: "Booster vaksin Coryza ketiga untuk perlindungan maksimal",
  },
]

// Detailed vaccine information
export const vaccineDetails: VaccineDetail[] = [
  {
    id: "nd-ib-live",
    name: "ND IB",
    fullName: "Newcastle Disease - Infectious Bronchitis (Live)",
    description:
      "Vaksin hidup untuk melindungi ayam dari Newcastle Disease (ND) dan Infectious Bronchitis (IB). Vaksin ini menggunakan strain virus yang dilemahkan sehingga dapat merangsang sistem kekebalan tanpa menyebabkan penyakit.",
    purpose: "Memberikan kekebalan aktif terhadap Newcastle Disease dan Infectious Bronchitis",
    targetDisease: ["Newcastle Disease (ND)", "Infectious Bronchitis (IB)"],
    applicationMethods: [
      {
        method: "Spray/tetes",
        description: "Aplikasi menggunakan spray atau tetes mata/hidung",
        steps: [
          "Siapkan vaksin yang sudah dicairkan dengan pelarut steril",
          "Untuk tetes mata: teteskan 1 tetes ke mata ayam",
          "Untuk spray: semprotkan vaksin merata ke kepala ayam",
          "Pastikan ayam dalam kondisi sehat saat vaksinasi",
          "Lakukan pada pagi atau sore hari saat suhu sejuk",
        ],
        tutorials: [
          {
            type: "video",
            src: "https://www.youtube.com/embed/vaccine-tetes-mata",
            caption: "Tutorial vaksinasi tetes mata",
          },
          {
            type: "image",
            src: "/tutorials/vaccine-eye-drop.jpg",
            caption: "Posisi yang benar saat meneteskan vaksin ke mata",
          },
        ],
      },
      {
        method: "Cekok",
        description: "Aplikasi langsung ke mulut ayam menggunakan pipet atau alat cekok",
        steps: [
          "Siapkan vaksin yang sudah dicairkan",
          "Buka paruh ayam dengan lembut",
          "Teteskan 0.03-0.05 ml vaksin ke pangkal lidah",
          "Pastikan vaksin tertelan sempurna",
          "Hindari mencekok saat ayam sedang minum",
        ],
        tutorials: [
          {
            type: "video",
            src: "https://www.youtube.com/embed/vaccine-cekok",
            caption: "Tutorial vaksinasi cekok yang benar",
          },
        ],
      },
      {
        method: "Air minum",
        description: "Vaksin dicampur ke dalam air minum",
        steps: [
          "Puasakan ayam dari air minum 1-2 jam sebelum vaksinasi",
          "Siapkan air matang yang sudah dingin (bukan air mentah)",
          "Larutkan vaksin sesuai dosis yang dianjurkan",
          "Tambahkan susu skim 2-3 gram/liter untuk menstabilkan vaksin",
          "Berikan segera setelah pencampuran, habiskan dalam 2 jam",
          "Pastikan semua ayam mendapat kesempatan minum",
        ],
        tutorials: [
          {
            type: "image",
            src: "/tutorials/vaccine-drinking-water.jpg",
            caption: "Persiapan vaksin air minum",
          },
        ],
      },
      {
        method: "Tetes mata",
        description: "Aplikasi langsung ke mata ayam",
        steps: [
          "Pegang ayam dengan lembut tapi mantap",
          "Miringkan kepala ayam ke samping",
          "Teteskan 1 tetes vaksin ke mata",
          "Tunggu sampai ayam berkedip untuk memastikan vaksin masuk",
          "Lepaskan ayam dengan hati-hati",
        ],
        tutorials: [
          {
            type: "video",
            src: "https://www.youtube.com/embed/vaccine-eye-drop",
            caption: "Teknik vaksinasi tetes mata",
          },
        ],
      },
    ],
    dosage: "0.03 ml per ekor untuk tetes, atau sesuai petunjuk produsen",
    storage: "Simpan di suhu 2-8°C, jauhkan dari sinar matahari langsung",
    precautions: [
      "Jangan vaksinasi ayam yang sedang sakit",
      "Gunakan vaksin dalam 2 jam setelah dilarutkan",
      "Hindari kontak vaksin dengan bahan desinfektan",
      "Jangan mencampur dengan antibiotik dalam air minum",
      "Cuci tangan setelah handling vaksin",
    ],
    sideEffects: [
      "Reaksi ringan seperti bersin-bersin selama 2-3 hari",
      "Penurunan nafsu makan sementara",
      "Mata berair ringan",
    ],
    notes:
      "Vaksin ND IB adalah vaksin dasar yang wajib diberikan kepada semua jenis ayam untuk perlindungan terhadap penyakit pernapasan.",
  },
  {
    id: "nd-g7-ib-ai-killed",
    name: "ND G7 IB AI",
    fullName: "Newcastle Disease G7 - Infectious Bronchitis - Avian Influenza (Killed/Inaktif)",
    description:
      "Vaksin inaktif (killed) yang memberikan perlindungan jangka panjang terhadap Newcastle Disease, Infectious Bronchitis, dan Avian Influenza. Vaksin ini mengandung virus yang sudah dimatikan sehingga tidak dapat berkembang biak di tubuh ayam.",
    purpose:
      "Memberikan kekebalan pasif dan antibodi tinggi untuk perlindungan jangka panjang terhadap ND, IB, dan AI",
    targetDisease: ["Newcastle Disease (ND)", "Infectious Bronchitis (IB)", "Avian Influenza (AI/Flu Burung)"],
    applicationMethods: [
      {
        method: "Sub Cutan",
        description: "Penyuntikan di bawah kulit, biasanya di leher bagian belakang",
        steps: [
          "Siapkan vaksin dan biarkan mencapai suhu ruang",
          "Kocok botol vaksin dengan lembut sebelum digunakan",
          "Ambil vaksin dengan spuit steril",
          "Tarik kulit di bagian belakang leher",
          "Tusukkan jarum di bawah kulit dengan sudut 45 derajat",
          "Suntikkan vaksin 0.3-0.5 ml per ekor",
          "Cabut jarum dan tekan ringan bekas suntikan",
        ],
        tutorials: [
          {
            type: "video",
            src: "https://www.youtube.com/embed/vaccine-subcutan",
            caption: "Tutorial vaksinasi subcutan di leher",
          },
          {
            type: "image",
            src: "/tutorials/vaccine-subcutan.jpg",
            caption: "Posisi suntik subcutan yang benar",
          },
        ],
      },
      {
        method: "Suntik dada",
        description: "Penyuntikan intramuskular di otot dada",
        steps: [
          "Siapkan vaksin pada suhu ruang",
          "Raba otot dada (pectoralis) untuk menentukan titik suntik",
          "Bersihkan area suntik jika diperlukan",
          "Tusukkan jarum ke otot dada dengan sudut 90 derajat",
          "Suntikkan vaksin 0.3-0.5 ml",
          "Cabut jarum dengan cepat",
        ],
        tutorials: [
          {
            type: "video",
            src: "https://www.youtube.com/embed/vaccine-chest-injection",
            caption: "Tutorial vaksinasi suntik dada (intramuskular)",
          },
        ],
      },
      {
        method: "Suntik dada/paha",
        description: "Penyuntikan intramuskular di otot dada atau paha",
        steps: [
          "Pilih lokasi suntik: otot dada atau otot paha",
          "Untuk dada: suntik di area otot pectoralis",
          "Untuk paha: suntik di area otot vastus lateralis",
          "Pastikan kedalaman jarum mencapai otot (1-1.5 cm)",
          "Suntikkan vaksin 0.5 ml per ekor",
        ],
        tutorials: [
          {
            type: "image",
            src: "/tutorials/vaccine-thigh-injection.jpg",
            caption: "Titik suntik paha yang tepat",
          },
        ],
      },
    ],
    dosage: "0.3-0.5 ml per ekor, sesuai petunjuk produsen",
    storage: "Simpan di suhu 2-8°C, jangan dibekukan, kocok sebelum digunakan",
    precautions: [
      "Jangan membekukan vaksin inaktif",
      "Kocok vaksin sebelum digunakan",
      "Gunakan jarum steril untuk setiap 500-1000 ekor",
      "Jangan menyuntik ke dalam pembuluh darah",
      "Vaksin harus habis dalam 1 hari setelah botol dibuka",
    ],
    sideEffects: [
      "Bengkak ringan di tempat suntikan (normal, akan hilang dalam 1-2 minggu)",
      "Penurunan produksi telur sementara (1-3 hari)",
      "Granuloma kecil di tempat suntikan pada beberapa kasus",
    ],
    notes:
      "Vaksin killed memberikan antibodi yang lebih tinggi dan tahan lama dibanding vaksin live, namun perlu disuntikkan satu per satu.",
  },
  {
    id: "gumboro-a",
    name: "Gumboro A",
    fullName: "Infectious Bursal Disease Vaccine Type A (Intermediate)",
    description:
      "Vaksin Gumboro tipe A (intermediate) untuk perlindungan awal terhadap Infectious Bursal Disease (IBD). Vaksin ini menggunakan strain intermediate yang cukup kuat untuk mengatasi antibodi maternal.",
    purpose: "Memberikan kekebalan aktif terhadap Infectious Bursal Disease (Gumboro)",
    targetDisease: ["Infectious Bursal Disease (IBD/Gumboro)"],
    applicationMethods: [
      {
        method: "Cekok",
        description: "Pemberian langsung ke mulut ayam",
        steps: [
          "Larutkan vaksin dengan pelarut steril sesuai dosis",
          "Siapkan alat cekok atau pipet steril",
          "Buka paruh ayam dengan hati-hati",
          "Teteskan 0.03 ml vaksin ke pangkal lidah",
          "Pastikan vaksin tertelan",
          "Lakukan dengan cepat untuk mengurangi stress",
        ],
        tutorials: [
          {
            type: "video",
            src: "https://www.youtube.com/embed/gumboro-vaccine",
            caption: "Tutorial vaksinasi Gumboro dengan cekok",
          },
        ],
      },
    ],
    dosage: "0.03 ml per ekor atau sesuai petunjuk produsen",
    storage: "Simpan di suhu 2-8°C, gunakan segera setelah dilarutkan",
    precautions: [
      "Berikan saat antibodi maternal mulai menurun (hari 10-14)",
      "Jangan berikan bersamaan dengan vaksin ND",
      "Interval minimal 5 hari dari vaksin lain",
      "Hindari vaksinasi saat cuaca panas",
    ],
    sideEffects: [
      "Imunosupresi ringan selama 3-5 hari",
      "Penurunan nafsu makan sementara",
    ],
    notes:
      "Gumboro menyerang bursa Fabricius yang merupakan organ kekebalan utama pada ayam muda, sehingga vaksinasi sangat penting.",
  },
  {
    id: "gumboro-b",
    name: "Gumboro B",
    fullName: "Infectious Bursal Disease Vaccine Type B (Intermediate Plus)",
    description:
      "Vaksin Gumboro tipe B (intermediate plus) sebagai booster untuk memperkuat kekebalan terhadap IBD. Strain yang digunakan lebih kuat dari tipe A untuk memberikan perlindungan yang lebih solid.",
    purpose: "Memperkuat kekebalan terhadap Infectious Bursal Disease setelah vaksinasi pertama",
    targetDisease: ["Infectious Bursal Disease (IBD/Gumboro)"],
    applicationMethods: [
      {
        method: "Cekok",
        description: "Pemberian langsung ke mulut ayam sebagai booster",
        steps: [
          "Larutkan vaksin dengan pelarut sesuai dosis",
          "Gunakan pipet atau alat cekok yang bersih",
          "Buka paruh ayam",
          "Teteskan vaksin ke pangkal lidah",
          "Pastikan vaksin masuk ke saluran pencernaan",
        ],
        tutorials: [
          {
            type: "video",
            src: "https://www.youtube.com/embed/gumboro-b-vaccine",
            caption: "Teknik vaksinasi Gumboro B",
          },
        ],
      },
    ],
    dosage: "0.03 ml per ekor",
    storage: "Simpan di suhu 2-8°C",
    precautions: [
      "Berikan 10 hari setelah vaksin Gumboro A",
      "Ayam harus dalam kondisi sehat",
      "Jangan berikan antibiotik 2 hari sebelum dan sesudah vaksinasi",
    ],
    notes:
      "Booster Gumboro sangat penting untuk memastikan perlindungan optimal, terutama di daerah dengan tekanan infeksi tinggi.",
  },
  {
    id: "pox",
    name: "POX",
    fullName: "Fowl Pox Vaccine (Cacar Unggas)",
    description:
      "Vaksin untuk melindungi ayam dari cacar unggas (fowl pox). Vaksin ini diberikan melalui tusuk sayap dan akan membentuk take (bekas tusukan yang membengkak) sebagai tanda vaksinasi berhasil.",
    purpose: "Memberikan kekebalan aktif terhadap penyakit cacar unggas",
    targetDisease: ["Fowl Pox (Cacar Unggas)"],
    applicationMethods: [
      {
        method: "Tusuk sayap",
        description: "Pemberian dengan menusukkan jarum khusus ke lipatan sayap",
        steps: [
          "Siapkan vaksin dan jarum tusuk sayap khusus (bifurcated needle)",
          "Celupkan jarum ke dalam vaksin",
          "Rentangkan sayap ayam untuk melihat lipatan kulit (web wing)",
          "Tusukkan jarum menembus lipatan kulit sayap",
          "Pastikan jarum menembus kulit tapi tidak mengenai tulang atau pembuluh darah",
          "Vaksin akan diserap melalui luka tusukan",
          "Cek 'take' setelah 7-10 hari (bengkak kecil di tempat tusukan)",
        ],
        tutorials: [
          {
            type: "video",
            src: "https://www.youtube.com/embed/fowl-pox-vaccine",
            caption: "Tutorial lengkap vaksinasi cacar unggas",
          },
          {
            type: "image",
            src: "/tutorials/wing-web-vaccination.jpg",
            caption: "Lokasi tusuk sayap yang benar",
          },
          {
            type: "image",
            src: "/tutorials/pox-vaccine-take.jpg",
            caption: "Contoh 'take' vaksin POX yang berhasil",
          },
        ],
      },
    ],
    dosage: "1 dosis per ekor melalui tusuk sayap",
    storage: "Simpan di suhu 2-8°C, terlindung dari cahaya",
    precautions: [
      "Gunakan jarum bifurcated yang bersih",
      "Hindari menusuk pembuluh darah atau tulang",
      "Jangan vaksinasi ayam yang sedang bertelur (vaksinasi sebelum produksi)",
      "Periksa 'take' setelah 7-10 hari untuk memastikan keberhasilan",
      "Vaksinasi ulang ayam yang tidak menunjukkan 'take'",
    ],
    sideEffects: [
      "Pembengkakan di tempat tusukan (take) - ini normal dan diharapkan",
      "Demam ringan selama 1-2 hari",
    ],
    notes:
      "Vaksin POX harus diberikan sebelum ayam masuk masa produksi. 'Take' yang muncul 7-10 hari setelah vaksinasi menunjukkan vaksin berhasil.",
  },
  {
    id: "coryza",
    name: "Coryza",
    fullName: "Infectious Coryza Vaccine (Snot)",
    description:
      "Vaksin inaktif untuk melindungi ayam dari penyakit Coryza (Snot/pilek menular). Penyakit ini menyebabkan pembengkakan wajah, keluar lendir dari hidung, dan penurunan produksi telur yang signifikan.",
    purpose: "Memberikan kekebalan terhadap Infectious Coryza yang disebabkan bakteri Avibacterium paragallinarum",
    targetDisease: ["Infectious Coryza (Snot/Pilek Menular)"],
    applicationMethods: [
      {
        method: "Suntik dada",
        description: "Penyuntikan intramuskular di otot dada",
        steps: [
          "Kocok vaksin sebelum digunakan",
          "Siapkan spuit dan jarum steril ukuran 21-22G",
          "Raba otot dada untuk menentukan titik suntik",
          "Suntikkan vaksin 0.5 ml ke otot dada",
          "Hindari suntik terlalu dalam atau dangkal",
        ],
        tutorials: [
          {
            type: "video",
            src: "https://www.youtube.com/embed/coryza-vaccine-chest",
            caption: "Vaksinasi Coryza di dada",
          },
        ],
      },
      {
        method: "Suntik paha",
        description: "Penyuntikan intramuskular di otot paha",
        steps: [
          "Kocok vaksin hingga homogen",
          "Siapkan spuit dengan jarum steril",
          "Tentukan lokasi suntik di otot paha bagian luar",
          "Suntikkan vaksin 0.5 ml dengan sudut 90 derajat",
          "Cabut jarum dan tekan ringan bekas suntikan",
        ],
        tutorials: [
          {
            type: "video",
            src: "https://www.youtube.com/embed/coryza-vaccine-thigh",
            caption: "Vaksinasi Coryza di paha",
          },
          {
            type: "image",
            src: "/tutorials/coryza-injection-site.jpg",
            caption: "Lokasi suntik paha untuk Coryza",
          },
        ],
      },
    ],
    dosage: "0.5 ml per ekor",
    storage: "Simpan di suhu 2-8°C, jangan dibekukan",
    precautions: [
      "Vaksinasi Coryza memerlukan 3 kali pemberian untuk perlindungan optimal",
      "Interval antar vaksinasi Coryza minimal 4 minggu",
      "Jangan mencampur dengan vaksin lain dalam satu spuit",
      "Ganti jarum setiap 500-1000 ekor",
    ],
    sideEffects: [
      "Bengkak di tempat suntikan (normal)",
      "Penurunan nafsu makan sementara (1-2 hari)",
      "Penurunan produksi telur ringan",
    ],
    notes:
      "Program vaksinasi Coryza yang lengkap (3 kali) sangat penting karena penyakit ini sangat menular dan menyebabkan kerugian ekonomi besar.",
  },
  {
    id: "ilt",
    name: "ILT",
    fullName: "Infectious Laryngotracheitis Vaccine",
    description:
      "Vaksin untuk melindungi ayam dari Infectious Laryngotracheitis (ILT), penyakit pernapasan serius yang menyerang saluran napas atas. Penyakit ini menyebabkan ayam sulit bernapas, batuk berdarah, dan dapat menyebabkan kematian tinggi.",
    purpose: "Memberikan kekebalan terhadap Infectious Laryngotracheitis",
    targetDisease: ["Infectious Laryngotracheitis (ILT)"],
    applicationMethods: [
      {
        method: "Cekok",
        description: "Pemberian langsung ke mulut/tenggorokan ayam",
        steps: [
          "Larutkan vaksin dengan pelarut steril",
          "Siapkan pipet atau alat cekok",
          "Buka paruh ayam",
          "Teteskan vaksin ke pangkal lidah atau dinding faring",
          "Pastikan vaksin mencapai area tenggorokan",
        ],
        tutorials: [
          {
            type: "video",
            src: "https://www.youtube.com/embed/ilt-vaccine",
            caption: "Tutorial vaksinasi ILT",
          },
        ],
      },
    ],
    dosage: "0.03 ml per ekor atau sesuai petunjuk produsen",
    storage: "Simpan di suhu 2-8°C, gunakan segera setelah dilarutkan",
    precautions: [
      "Vaksin ILT hanya diberikan di daerah endemis",
      "Jangan bersamaan dengan vaksin pernapasan lain (interval minimal 2 minggu)",
      "Pekerja yang handling vaksin harus menggunakan masker",
      "Ayam yang divaksin dapat mengeluarkan virus vaksin selama beberapa minggu",
    ],
    sideEffects: [
      "Reaksi pernapasan ringan (bersin, ngorok)",
      "Mata berair",
      "Penurunan nafsu makan sementara",
    ],
    notes:
      "Vaksin ILT bersifat laten dan dapat menyebar ke ayam yang tidak divaksin. Hanya berikan jika penyakit ILT ada di wilayah peternakan.",
  },
  {
    id: "triple-nd-ib-eds",
    name: "Triple ND+IB+EDS",
    fullName: "Newcastle Disease + Infectious Bronchitis + Egg Drop Syndrome Vaccine",
    description:
      "Vaksin inaktif triple yang melindungi ayam petelur dari tiga penyakit sekaligus: Newcastle Disease, Infectious Bronchitis, dan Egg Drop Syndrome. Vaksin ini wajib untuk ayam layer sebelum memasuki masa produksi.",
    purpose: "Memberikan perlindungan komprehensif untuk ayam layer menjelang produksi telur",
    targetDisease: ["Newcastle Disease (ND)", "Infectious Bronchitis (IB)", "Egg Drop Syndrome (EDS)"],
    applicationMethods: [
      {
        method: "Suntik dada/paha",
        description: "Penyuntikan intramuskular di otot dada atau paha",
        steps: [
          "Bawa vaksin ke suhu ruang sebelum digunakan",
          "Kocok botol vaksin dengan baik",
          "Gunakan spuit dan jarum steril (21-22G)",
          "Pilih lokasi suntik: dada atau paha",
          "Suntikkan 0.5 ml vaksin secara intramuskular",
          "Hindari menyuntik ke lemak atau kulit",
        ],
        tutorials: [
          {
            type: "video",
            src: "https://www.youtube.com/embed/triple-vaccine-layer",
            caption: "Vaksinasi triple untuk layer",
          },
          {
            type: "image",
            src: "/tutorials/layer-vaccination.jpg",
            caption: "Teknik vaksinasi layer sebelum produksi",
          },
        ],
      },
    ],
    dosage: "0.5 ml per ekor",
    storage: "Simpan di suhu 2-8°C, jangan dibekukan, kocok sebelum digunakan",
    precautions: [
      "Berikan 2-4 minggu sebelum ayam mulai bertelur",
      "Jangan berikan pada ayam yang sudah berproduksi tanpa konsultasi dokter hewan",
      "Interval minimal 2 minggu dari vaksin lain",
      "Pastikan ayam dalam kondisi prima saat divaksin",
    ],
    sideEffects: [
      "Bengkak di tempat suntikan",
      "Penurunan produksi telur sementara jika diberikan saat sudah bertelur",
      "Lesu selama 1-2 hari",
    ],
    notes:
      "EDS (Egg Drop Syndrome) menyebabkan penurunan produksi dan kualitas telur. Vaksin triple ini sangat penting untuk menjaga produktivitas layer.",
  },
]

// Helper function to get vaccine detail by ID
export function getVaccineById(id: string): VaccineDetail | undefined {
  return vaccineDetails.find((v) => v.id === id)
}

// Helper function to format age display
export function formatAge(dayMin: number, weekMin?: number): string {
  if (weekMin) {
    return `Minggu ${weekMin}`
  }
  return `Hari ${dayMin}`
}
